import io from "socket.io-client";
import api from "../services/api";

const getLatestToken = () => {
  let sessionToken = api.defaults.headers.Authorization;
  if (!sessionToken && localStorage.getItem("token")) {
    try {
      const parsed = JSON.parse(localStorage.getItem("token"));
      const raw = typeof parsed === "string" ? parsed : String(parsed);
      sessionToken = raw.startsWith("Bearer ") ? raw : `Bearer ${raw}`;
    } catch {
      const raw = localStorage.getItem("token").replace(/^"(.*)"$/, "$1");
      sessionToken = raw.startsWith("Bearer ") ? raw : `Bearer ${raw}`;
    }
  }
  return sessionToken;
};

class SocketWorker {
  constructor(companyId, userId) {
    if (!SocketWorker.instance) {
      this.companyId = companyId;
      this.userId = userId;
      this.socket = null;
      this.eventListeners = {};
      this._reconnectAttempts = 0;
      this._reconnectTimer = null;
      this.configureSocket();
      SocketWorker.instance = this;
    } else if (companyId && userId && SocketWorker.instance.companyId !== companyId) {
      SocketWorker.instance.companyId = companyId;
      SocketWorker.instance.userId = userId;
      SocketWorker.instance._reconnectAttempts = 0;
      if (SocketWorker.instance.socket) {
        SocketWorker.instance.socket.disconnect();
        SocketWorker.instance.configureSocket();
      }
    }

    return SocketWorker.instance;
  }

  configureSocket() {
    // Sempre usa o token mais recente disponível
    const currentToken = getLatestToken();

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }

    this.socket = io(`${process.env.REACT_APP_BACKEND_URL}/${this?.companyId}`, {
      autoConnect: true,
      reconnection: false, // Gerenciamos a reconexão manualmente para controlar o token
      transports: ["websocket", "polling"],
      extraHeaders: {
        "ngrok-skip-browser-warning": "true"
      },
      query: {
        userId: this.userId,
        token: currentToken,
        "ngrok-skip-browser-warning": "true"
      }
    });

    this.socket.on("connect", () => {
      console.log("Conectado ao servidor Socket.IO");
      this._reconnectAttempts = 0; // Resetar tentativas ao conectar com sucesso
      this._reRegisterListeners();
    });

    this.socket.on("disconnect", (reason) => {
      console.log("Desconectado do servidor Socket.IO, razão:", reason);
      // Só reconectar se não foi desconexão manual
      if (reason !== "io client disconnect") {
        this._scheduleReconnect();
      }
    });

    this.socket.on("connect_error", (err) => {
      console.warn("Erro de conexão Socket.IO:", err?.message || err);
      this._scheduleReconnect();
    });

    this._reRegisterListeners();
  }

  _scheduleReconnect() {
    if (this._reconnectTimer) return;

    // Backoff exponencial: 1s, 2s, 4s, 8s, máximo 30s
    const delay = Math.min(1000 * Math.pow(2, this._reconnectAttempts), 30000);
    this._reconnectAttempts++;

    console.log(`Socket: reagendando reconexão em ${delay}ms (tentativa ${this._reconnectAttempts})`);

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      // Verificar se ainda há token válido antes de reconectar
      const freshToken = getLatestToken();
      if (!freshToken) {
        console.log("Socket: sem token disponível, abortando reconexão");
        this._reconnectAttempts = 0;
        return;
      }
      console.log("Socket: reconectando com token atualizado");
      this.configureSocket();
    }, delay);
  }

  // Re-registra todos os listeners armazenados no socket atual
  _reRegisterListeners() {
    if (!this.socket) return;
    Object.entries(this.eventListeners).forEach(([event, callbacks]) => {
      callbacks.forEach(callback => {
        this.socket.off(event, callback);
        this.socket.on(event, callback);
      });
    });
  }

  // Adiciona um ouvinte de eventos
  on(event, callback) {
    this.connect();
    this.socket.on(event, callback);

    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    if (!this.eventListeners[event].includes(callback)) {
      this.eventListeners[event].push(callback);
    }
  }

  // Emite um evento
  emit(event, data) {
    this.connect();
    this.socket.emit(event, data);
  }

  // Desconecta um ou mais ouvintes de eventos
  off(event, callback) {
    if (this.eventListeners[event]) {
      if (callback) {
        if (this.socket) this.socket.off(event, callback);
        this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
      } else {
        if (this.socket) this.eventListeners[event].forEach(cb => this.socket.off(event, cb));
        delete this.eventListeners[event];
      }
    }
  }

  disconnect() {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    SocketWorker.instance = null;
    console.log("Socket desconectado manualmente");
  }

  // Garante que o socket esteja conectado
  connect() {
    if (!this.socket) {
      this.configureSocket();
    } else if (!this.socket.connected && !this.socket.connecting) {
      this.socket.connect();
    }
  }

  forceReconnect() {
    this._reconnectAttempts = 0;
    this.configureSocket();
  }
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
